<script setup>
import {computed, nextTick, onMounted, ref, watch} from 'vue'
import helpers from '@/lib/helpers'

// Bulk tagging: a record type, a list of ids off a notepad, and the changes to
// make. Preview first - the ids are typed by hand, so nothing is written until
// the tool has said what it matched and what each record would end up with.

const types = ref([])
const type = ref(null)
const ids = ref('')

const doClear = ref(false)
const doRemove = ref(false)
const doAdd = ref(false)
const removeTags = ref('')
const addTags = ref('')

const preview = ref(null)
const busy = ref(false)
const tab = ref(0)

/** However it got pasted - commas, spaces or new lines. */
const splitIds = (raw) => (raw || '').split(/[\s,;]+/).filter(Boolean)

/**
 * Counts come off the rows rather than the server's summary, so dropping a row
 * from the preview keeps the totals honest without another round trip.
 */
const stats = computed(() => {
  const rows = (preview.value && preview.value.rows) || []

  return {
    requested: rows.length,
    matched: rows.filter(r => r.found).length,
    missing: rows.filter(r => !r.found).length,
    changed: rows.filter(r => r.changed).length,
  }
})

const selectedType = computed(() => types.value.find(t => t.key === type.value) || null)
const typeOptions = computed(() => types.value.map(t => ({value: t.key, text: t.label})))

const hasAction = computed(() =>
    doClear.value || (doRemove.value && removeTags.value.trim()) || (doAdd.value && addTags.value.trim()))

const canPreview = computed(() => !!type.value && !!ids.value.trim() && hasAction.value && !busy.value)

/** What gets sent - shared by preview and process so they cannot disagree. */
const payload = () => ({
  type: type.value,
  ids: ids.value,
  clear: doClear.value ? 1 : 0,
  remove: doRemove.value ? removeTags.value : '',
  add: doAdd.value ? addTags.value : '',
})

// any edit invalidates the preview, so Process can never apply something other
// than what was shown. Dropping a row from the preview is the one exception -
// it edits the id list and the rows together, which leaves the two consistent.
let syncing = false
watch([type, ids, doClear, doRemove, doAdd, removeTags, addTags], () => {
  if (syncing)
    return

  preview.value = null
  tab.value = 0
})

const runPreview = async () => {
  busy.value = true
  try {
    preview.value = await helpers.asyncAction('/api/tagtool/preview', 'POST', {data: payload()})

    // after the re-render, not before: the Preview tab is :disabled until there
    // is a preview, and b-tabs refuses to activate a tab it still believes is
    // disabled - setting the index in the same tick silently snaps back to 0
    await nextTick()
    tab.value = 1
  } finally {
    busy.value = false
  }
}

/** Drop one record from this run - takes its id out of the list as well. */
const removeRow = (row) => {
  syncing = true

  ids.value = splitIds(ids.value).filter(t => t !== row.input).join(', ')
  preview.value = {...preview.value, rows: preview.value.rows.filter(r => r !== row)}

  nextTick(() => syncing = false)
}

const process = async () => {
  const p = preview.value
  if (!p || !stats.value.changed) return

  if (!await helpers.confirm(`Apply tag changes to ${stats.value.changed} ${p.label.toLowerCase()}?`))
    return

  busy.value = true
  try {
    const res = await helpers.asyncAction('/api/tagtool', 'POST', {data: payload()})
    helpers.toast(`${res.changed} record${res.changed === 1 ? '' : 's'} updated`)
    syncing = true
    preview.value = res
    nextTick(() => syncing = false)
  } finally {
    busy.value = false
  }
}

/**
 * Click an existing tag to append it rather than retyping it.
 *
 * Takes the field NAME, not the ref: refs are auto-unwrapped in the template, so
 * passing `addTags` there hands over the string and the assignment goes nowhere.
 */
const appendTag = (which, tag) => {
  const field = which === 'add' ? addTags : removeTags
  const cur = field.value.trim()
  const list = cur ? cur.split(/[\s,;]+/).filter(Boolean) : []

  if (!list.includes(tag)) list.push(tag)

  field.value = list.join(', ')
}

onMounted(async () => {
  types.value = await helpers.asyncAction('/api/tagtool/type', 'GET')
  if (types.value.length === 1) type.value = types.value[0].key
})
</script>

<template>
  <modal size="xl" height="700px" title="Tag Tool">
    <b-tabs v-model="tab" card>
      <b-tab title="Records" no-body>
        <div class="p-3">
          <p class="text-muted small">
            Apply tag changes to many records at once. Paste GLIs or ids — <code>35-00006</code> and
            <code>6</code> both work — then preview before anything is written.
          </p>

          <b-form-group label="Record type" label-cols-sm="2" label-size="sm" class="mb-2">
            <b-form-select size="sm" v-model="type" :options="typeOptions" :disabled="!types.length">
              <template #first>
                <b-form-select-option :value="null" disabled>— choose a record type —</b-form-select-option>
              </template>
            </b-form-select>
          </b-form-group>

          <b-form-group label="Ids" label-cols-sm="2" label-size="sm" class="mb-3">
            <b-form-textarea size="sm" rows="4" max-rows="12" v-model="ids"
                             placeholder="35-00006, 35-00007, 122"/>
            <small class="text-muted">
              Separated by commas, spaces or new lines.
              <span v-if="splitIds(ids).length">{{ splitIds(ids).length }} id(s) entered.</span>
            </small>
          </b-form-group>

          <div class="border-top pt-3">
            <b-form-checkbox v-model="doClear" class="mb-2">
              Clear all tags
              <span class="text-muted small">— remove every tag on the matched records first</span>
            </b-form-checkbox>

            <b-form-checkbox v-model="doRemove" class="mb-1">Remove tags</b-form-checkbox>
            <div v-if="doRemove" class="ml-4 mb-3">
              <b-form-input size="sm" v-model="removeTags" placeholder="cisco, avaya"/>
              <div v-if="selectedType && selectedType.available.length" class="mt-1">
                <b-badge v-for="t in selectedType.available" :key="'r-' + t" variant="light"
                         class="mr-1 mb-1 cursor-pointer" @click="appendTag('remove', t)">{{ t }}</b-badge>
              </div>
            </div>

            <b-form-checkbox v-model="doAdd" class="mb-1">Add tags</b-form-checkbox>
            <div v-if="doAdd" class="ml-4 mb-3">
              <b-form-input size="sm" v-model="addTags" placeholder="cedia-26, followup"/>
              <small class="text-muted">A tag already on a record is left alone, never duplicated.</small>
              <div v-if="selectedType && selectedType.available.length" class="mt-1">
                <b-badge v-for="t in selectedType.available" :key="'a-' + t" variant="light"
                         class="mr-1 mb-1 cursor-pointer" @click="appendTag('add', t)">{{ t }}</b-badge>
              </div>
            </div>
          </div>
        </div>
      </b-tab>

      <b-tab :title="preview ? (preview.applied ? 'Result' : 'Preview') : 'Preview'"
             :disabled="!preview" no-body>
        <div v-if="preview" class="p-3">
          <div class="d-flex align-items-center mb-2">
            <b-badge variant="secondary" class="mr-1">{{ stats.matched }} matched</b-badge>
            <b-badge v-if="stats.missing" variant="danger" class="mr-1">{{ stats.missing }} not found</b-badge>
            <b-badge :variant="stats.changed ? 'primary' : 'light'">
              {{ stats.changed }} {{ preview.applied ? 'changed' : 'would change' }}
            </b-badge>
            <span v-if="!preview.applied" class="text-muted small ml-auto">
              Use the × to drop a record from this run.
            </span>
          </div>

          <b-table small sticky-header="520px" class="mb-0" :items="preview.rows" :fields="[
            {key: 'input', label: 'Id', tdClass: 'align-middle p-1 text-nowrap'},
            {key: 'description', label: 'Record', tdClass: 'align-middle p-1'},
            {key: 'tags', label: 'Now', tdClass: 'align-middle p-1'},
            {key: 'result', label: preview.applied ? 'Tags' : 'After', tdClass: 'align-middle p-1'},
            {key: 'tasks', label: '', tdClass: 'align-middle p-1 text-right text-nowrap'},
          ]">
            <template #cell(input)="{item}">
              <span :class="item.found ? '' : 'text-danger'">{{ item.gli || item.input }}</span>
            </template>
            <template #cell(description)="{item}">
              <span v-if="item.found" class="small">{{ item.description }}</span>
              <span v-else class="small text-danger">no matching record</span>
            </template>
            <template #cell(tags)="{item}">
              <b-badge v-for="t in item.tags" :key="t" variant="light" class="mr-1">{{ t }}</b-badge>
              <span v-if="item.found && !item.tags.length" class="text-muted small">—</span>
            </template>
            <template #cell(result)="{item}">
              <template v-if="item.found">
                <b-badge v-for="t in item.result" :key="t"
                         :variant="item.tags.includes(t) ? 'light' : 'primary'" class="mr-1">{{ t }}</b-badge>
                <span v-if="!item.result.length" class="text-muted small">—</span>
              </template>
            </template>
            <template #cell(tasks)="{item}">
              <b-button v-if="!preview.applied" size="sm" variant="link" class="text-danger p-0"
                        v-b-tooltip.hover title="Remove from this run" @click="removeRow(item)">
                <font-awesome-icon :icon="['fas', 'xmark']"/>
              </b-button>
            </template>
          </b-table>
        </div>
      </b-tab>
    </b-tabs>

    <template #footer>
      <b-btn size="sm" variant="outline-secondary" class="px-3 mr-2" :disabled="!canPreview" @click="runPreview">
        Preview
      </b-btn>
      <b-btn size="sm" variant="outline-primary" class="px-4"
             :disabled="busy || !preview || !stats.changed || preview.applied" @click="process">
        Process
      </b-btn>
    </template>
  </modal>
</template>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
